import { BreakdownGrid } from "@/components/BreakdownGrid";
import { ScreenHelp } from "@/components/Help";
import { ErrorState, LoadingState } from "@/components/States";
import { Card, Icon, Pill, Select, StatusBadge, TextInput } from "@/components/ui";
import { exportQuoteBundle } from "@/lib/api";
import { type BreakdownRow, avgAnnualVolume, cncBreakdown, cncPnL } from "@/lib/cost/cncBreakdown";
import type { ProjectionResult } from "@/lib/cost/projection";
import { STATUS, type StatusKey, customerNameFrom, fmt, formatContacts, isoDate } from "@/lib/data";
import { exportFileName, quoteDetailSheets } from "@/lib/excel";
import {
  updateQuote,
  updateQuoteStatus,
  useCncLaborRates,
  useCncRawMaterials,
  useCostMaster,
  useCustomers,
  useLossReasons,
  useQuote,
  useQuoteImageSrc,
} from "@/lib/hooks";
import { downloadXlsx } from "@/lib/xlsx";
import { type CSSProperties, type ReactNode, useEffect, useState } from "react";

const DETAIL_HELP = [
  "견적 1건의 상태 · 원가 분해 · 손익분석 · 버전/수정 이력을 확인합니다.",
  "상태 드롭다운으로 진행 상태를 변경합니다 — '수주 실패' 선택 시 실패 사유 입력이 요구됩니다.",
  "규격은 사용자가 직접 입력하는 유일한 값입니다 — 나머지 원가는 모두 이전 화면에서 자동 산출·연동됩니다.",
  "엑셀 · PDF 견적서 버튼으로 견적을 출력합니다. PDF 는 브라우저 인쇄(가로 A4)로 헤더밴드가 매 페이지 반복됩니다.",
  "하단 '버전' / '수정 이력' 탭에서 변경 내역을 추적합니다.",
];

// ---- 견적상세 분해표 셀 스타일 (대표님 가독성 — 행 높이·글자 크게) ----
// 인쇄 칸 구분: 전 셀 테두리 — --table-border 는 @media print 에서 잉크색으로 재매핑됨.
const TABLE_BORDER = "1px solid var(--table-border)";
const cell: CSSProperties = {
  padding: "22px 14px",
  textAlign: "right",
  fontFamily: "ui-monospace, monospace",
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
  border: TABLE_BORDER,
};
const th: CSSProperties = {
  padding: "14px 12px",
  fontWeight: 800,
  fontSize: 16.5,
  color: "var(--text-dim)",
  whiteSpace: "nowrap",
  border: TABLE_BORDER,
};
const SCALE_KEY = "qd_table_scale_v2";

// 이미지 첨부가 없는 행의 자리표시 박스.
const EmptyThumb = () => (
  <div
    className="qd-thumbbox"
    style={{
      width: 96,
      height: 68,
      margin: "0 auto",
      borderRadius: 6,
      border: "1px dashed var(--bg-surface-3)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "var(--text-faint)",
    }}
  >
    <Icon name="file" size={18} />
  </div>
);

// 도면/사진 썸네일 — 파일은 인증 헤더가 필요해 <img src> 직접 불가 → blob URL 로 로드
// (useQuoteImageSrc 공용 훅). 클릭 시 원본 크기 미리보기 오버레이(인쇄에는 썸네일만 출력,
// 오버레이는 qd-noprint).
// 가로 폭은 고정(도면 열 폭에 맞춤), 높이는 원본 비율 — contain 이라 잘리지 않고 전체가 보인다.
// 세로로 긴 도면이 행을 과하게 늘리지 않도록 최대 높이 제한(넘으면 비율 유지한 채 축소).
const QuoteThumb = ({ imageKey, alt }: { imageKey: string; alt: string }) => {
  const url = useQuoteImageSrc(imageKey);
  const [zoom, setZoom] = useState(false);
  if (!url) return <EmptyThumb />;
  return (
    <>
      <img
        className="qd-thumbimg"
        src={url}
        alt={alt}
        onClick={() => setZoom(true)}
        style={{
          width: 96,
          height: "auto",
          maxHeight: 160,
          objectFit: "contain",
          borderRadius: 6,
          border: "1px solid var(--bg-surface-3)",
          cursor: "zoom-in",
          display: "block",
          margin: "0 auto",
        }}
      />
      {zoom && (
        <div
          className="qd-noprint"
          onClick={() => setZoom(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.75)",
            zIndex: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "zoom-out",
            padding: 16,
          }}
        >
          <img
            src={url}
            alt={alt}
            style={{ maxWidth: "80vw", maxHeight: "80vh", borderRadius: 8 }}
          />
        </div>
      )}
    </>
  );
};

// 도면 PDF 칩 — 썸네일 슬롯(96×68)에 파일 아이콘 + PDF 라벨 + 파일명.
// 클릭 시 이미지 줌과 동일한 오버레이 패턴으로 iframe 미리보기(인쇄는 칩만, 오버레이 qd-noprint).
const QuotePdfThumb = ({ fileKey, name }: { fileKey: string; name: string }) => {
  const url = useQuoteImageSrc(fileKey);
  const [zoom, setZoom] = useState(false);
  return (
    <>
      <div
        className="qd-thumbbox"
        onClick={() => url && setZoom(true)}
        title={name}
        style={{
          width: 96,
          height: 68,
          margin: "0 auto",
          borderRadius: 6,
          border: "1px solid var(--bg-surface-3)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          cursor: url ? "zoom-in" : "default",
          color: "var(--text-dim)",
        }}
      >
        <Icon name="file" size={16} />
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.5px" }}>PDF</span>
        <span
          style={{
            fontSize: 9,
            maxWidth: 84,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: "var(--text-faint)",
          }}
        >
          {name}
        </span>
      </div>
      {zoom && url && (
        <div
          className="qd-noprint"
          onClick={() => setZoom(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.75)",
            zIndex: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "zoom-out",
            padding: 16,
          }}
        >
          <iframe
            src={url}
            title={name}
            style={{ width: "80vw", height: "85vh", border: "none", background: "#fff" }}
          />
        </div>
      )}
    </>
  );
};

// ---- 견적 상세 항목 (CNC 파트 — 고정 9열 표, 내용추가17 C안) ----
// 22열 분해표는 세부 항목·공정마다 열이 늘어 인쇄 시 셀이 좁아져 깨졌다 → 그룹 셀 안 세로 목록으로.
const BreakdownSection = ({
  rows,
  specs,
  onSpec,
  editable,
  files,
  partImages,
  band,
  procOrder,
}: {
  rows: BreakdownRow[];
  specs: string[];
  onSpec: (i: number, v: string) => void;
  editable: boolean;
  files: { key: string; name: string }[]; // 파트 순서대로 매핑된 첨부(이미지·PDF) — 없으면 빈 슬롯
  partImages: string[]; // 파트별 이미지 스토리지 키(견적등록 업로드, "" = 없음) — 첨부보다 우선
  band: ReactNode; // 인쇄 시 매 페이지 상단에 반복할 기본정보 밴드
  procOrder: readonly string[]; // 원가마스터 공정 노출순서(가공비 줄 정렬 기준). 빈 배열 = 고정 규칙
}) => {
  // 인쇄 가독성(내용추가5 R1): 글자배율 A−/A+ + 굵게. 로컬 저장, 인쇄엔 컨트롤 숨김.
  // 배율 저장 키는 v2 — 기본 크기를 낮추면서(카톡 피드백) 예전에 A+ 로 저장해 둔 배율을 초기화한다.
  const [scale, setScale] = useState<number>(() => {
    localStorage.removeItem("qd_table_scale");
    const v = Number(localStorage.getItem(SCALE_KEY));
    return v >= 0.8 && v <= 1.6 ? v : 1;
  });
  const [bold, setBold] = useState<boolean>(() => localStorage.getItem("qd_table_bold") === "1");
  useEffect(() => {
    localStorage.setItem(SCALE_KEY, String(scale));
  }, [scale]);
  useEffect(() => {
    localStorage.setItem("qd_table_bold", bold ? "1" : "0");
  }, [bold]);
  const clamp = (v: number) => Math.min(1.6, Math.max(0.8, Number(v.toFixed(2))));

  const ctlBtn: CSSProperties = {
    minWidth: 34,
    height: 30,
    padding: "0 10px",
    borderRadius: 8,
    border: "1px solid var(--ring-input)",
    background: "var(--bg-surface-2)",
    color: "var(--text-base)",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: 13,
    fontWeight: 700,
  };

  // 파트 이미지(견적등록 업로드) 우선, 없으면 첨부파일 순서 매핑 폴백.
  const thumb = (i: number): ReactNode =>
    partImages[i] ? (
      <QuoteThumb imageKey={partImages[i]} alt={`${rows[i].partNo} 파트 이미지`} />
    ) : files[i] ? (
      /\.pdf$/i.test(files[i].name) ? (
        <QuotePdfThumb fileKey={files[i].key} name={files[i].name} />
      ) : (
        <QuoteThumb imageKey={files[i].key} alt={`${rows[i].partNo} 도면/사진`} />
      )
    ) : (
      <EmptyThumb />
    );

  const spec = (i: number): ReactNode =>
    editable ? (
      <>
        {/* 인쇄는 입력폼 대신 평문으로 출력 */}
        <span className="qd-printonly">{specs[i] || "-"}</span>
        <span className="qd-noprint" style={{ display: "inline-block", verticalAlign: "middle" }}>
          <TextInput
            value={specs[i] ?? ""}
            onChange={(v) => onSpec(i, v)}
            placeholder="규격 입력"
            style={{ height: 26, width: 104, padding: "0 8px" }}
          />
        </span>
      </>
    ) : (
      specs[i] || "-"
    );

  const head = (
    <div
      className="qd-sectitle"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
        padding: "0 0 12px",
      }}
    >
      <div style={{ fontFamily: "var(--font-title)", fontWeight: 800, fontSize: 16 }}>
        견적 상세 항목{" "}
        <span
          style={{
            fontSize: 11.5,
            color: "var(--text-faint)",
            fontWeight: 400,
            letterSpacing: "0.5px",
          }}
        >
          · 규격 외 전 항목 자동 산출 · 생성 시점 스냅샷
        </span>
      </div>
      {/* 인쇄 가독성 컨트롤 — 화면 전용(인쇄 시 숨김) */}
      <div
        className="qd-noprint"
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 8,
          marginLeft: "auto",
          color: "var(--text-muted)",
          fontSize: 12.5,
        }}
      >
        <span style={{ fontWeight: 700 }}>글자 크기</span>
        <button type="button" style={ctlBtn} onClick={() => setScale((s) => clamp(s - 0.1))}>
          A−
        </button>
        <button type="button" style={ctlBtn} onClick={() => setScale(1)}>
          기본
        </button>
        <button type="button" style={ctlBtn} onClick={() => setScale((s) => clamp(s + 0.1))}>
          A+
        </button>
        <button
          type="button"
          aria-pressed={bold}
          style={{
            ...ctlBtn,
            background: bold ? "var(--brand)" : "var(--bg-surface-2)",
            color: bold ? "#fff" : "var(--text-base)",
          }}
          onClick={() => setBold((b) => !b)}
        >
          굵게
        </button>
        <span style={{ marginLeft: 2 }}>{Math.round(scale * 100)}%</span>
      </div>
    </div>
  );

  return (
    <BreakdownGrid
      rows={rows}
      procOrder={procOrder}
      head={head}
      band={band}
      thumb={thumb}
      spec={spec}
      scale={scale}
      bold={bold}
    />
  );
};

export const DetailScreen = ({
  quoteId,
  onToast,
  onEditQuote,
  onCustomerDoc,
  onBack,
}: {
  quoteId: string | null;
  onToast: (msg: string) => void;
  onEditQuote: (id: string) => void;
  onCustomerDoc: (id: string) => void;
  onBack: () => void; // 견적 리스트로 복귀 — 직전 조회 조건(고객사·기간·검색어)은 그대로 유지된다.
}) => {
  const quoteRes = useQuote(quoteId);
  const customersRes = useCustomers();
  const lossReasonsRes = useLossReasons();
  const cncRaw = useCncRawMaterials();
  const cncLabor = useCncLaborRates();
  const costMaster = useCostMaster();
  const base = quoteRes.data;

  const [lossReason, setLossReason] = useState("");
  const [askLoss, setAskLoss] = useState(false);
  const [tab, setTab] = useState<"versions" | "logs">("versions");
  const [pending, setPending] = useState(false);

  // 규격(파트별) — 이 화면에서 사용자가 직접 편집하는 유일한 값. 저장 시 cnc 패치.
  const [specs, setSpecs] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (!base) return;
    setSpecs(base.cnc.map((p) => p.selection.spec ?? ""));
    setDirty(false);
  }, [base]);

  const commitStatus = async (k: StatusKey, reason?: string) => {
    if (!base) return;
    setPending(true);
    try {
      await updateQuoteStatus(base.id, k, reason);
      await quoteRes.reload();
      onToast(
        k === "lost"
          ? "수주 실패로 변경되었습니다"
          : `상태가 '${STATUS[k].label}'(으)로 변경되었습니다`
      );
    } catch (e) {
      onToast(e instanceof Error ? e.message : "상태 변경에 실패했습니다");
    } finally {
      setPending(false);
    }
  };

  const changeStatus = (v: string) => {
    const k = v as StatusKey;
    if (k === "lost") {
      setLossReason("");
      setAskLoss(true);
    } else {
      void commitStatus(k);
    }
  };

  const saveEdits = async () => {
    if (!base) return;
    setPending(true);
    try {
      const cnc = base.cnc.map((p, i) => ({
        ...p,
        selection: { ...p.selection, spec: specs[i] ?? "" },
      }));
      await updateQuote(base.id, { ...base, cnc });
      await quoteRes.reload();
      onToast("규격을 저장했습니다");
    } catch (e) {
      onToast(e instanceof Error ? e.message : "저장에 실패했습니다");
    } finally {
      setPending(false);
    }
  };

  if (quoteRes.loading) {
    return (
      <div style={{ padding: "24px 28px 56px" }}>
        <LoadingState />
      </div>
    );
  }
  if (quoteRes.error) {
    return (
      <div style={{ padding: "24px 28px 56px" }}>
        <ErrorState error={quoteRes.error} onRetry={() => void quoteRes.reload()} />
      </div>
    );
  }
  if (!base) {
    return (
      <div
        style={{
          padding: "72px 28px",
          textAlign: "center",
          color: "var(--text-faint)",
          fontSize: 14,
        }}
      >
        견적을 찾을 수 없습니다
      </div>
    );
  }

  const status = base.status;
  const cust = customersRes.data.find((c) => c.id === base.customerId);
  const custName = customerNameFrom(customersRes.data, base.customerId);

  // CNC 마스터에서 파트별 재질·공법 라벨 resolve (분해표/손익표 입력).
  const labels = base.cnc.map((p) => ({
    material: cncRaw.data.find((m) => m.id === p.selection.materialId)?.name ?? "",
    method: cncLabor.data.find((l) => l.id === p.selection.rows[0]?.laborRateId)?.process ?? "",
  }));
  // 분해표 공정 컬럼 순서 = 원가마스터의 공정 노출순서. 0(미지정, 서버 응답 전 fallback)
  // 은 제외 → 빈 배열이면 processColumns 가 통합 전 고정 규칙으로 정렬한다.
  const procOrder = costMaster.data.processes
    .filter((p) => p.displayOrder > 0)
    .slice()
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((p) => p.key);
  const hasCnc = base.cnc.length > 0;
  const breakdown = hasCnc ? cncBreakdown(base.cnc, labels) : null;
  const pnl = hasCnc ? cncPnL(base.cnc, labels) : null;
  const avgVol = hasCnc ? avgAnnualVolume(base.cnc) : 0;

  // 도면/사진 — 첨부 중 이미지·PDF 파일을 파트 순서대로 매핑(i번째 파트 ← i번째 첨부).
  const rowFiles = base.attachments.filter((a) => /\.(png|jpe?g|gif|webp|pdf)$/i.test(a.name));

  const versions = base.versions.length
    ? base.versions
    : [{ v: "v1", at: `${base.created} 00:00`, by: base.owner, note: "최초 생성" }];

  const setSpec = (i: number, v: string) => {
    setSpecs((prev) => prev.map((s, j) => (j === i ? v : s)));
    setDirty(true);
  };

  // 헤더밴드 — 고객사/프로젝트/제출용도/총아이템/연평균물량/SOP. 인쇄 시엔 상단 카드 대신
  // 견적 상세 항목 표의 <thead> 로 들어가 매 페이지 상단에 반복된다(BreakdownGrid).
  // 대표님 가독성: 기존 대비 2배 크기, 연평균물량은 브랜드색 강조.
  // 인쇄 칸 구분: 항목별 셀 테두리(-1px 겹침으로 collapse 룩) — --table-border 는 print 재매핑.
  const HeaderBand = () => (
    <div className="qd-band" style={{ display: "flex", flexWrap: "wrap" }}>
      {(
        [
          ["고객사", custName, false],
          ["프로젝트", base.project, false],
          ["제출 용도", base.purpose || "-", false],
          ["총 아이템(EA)", hasCnc ? String(base.cnc.length) : String(base.items.length), false],
          ["연 평균 물량(EA)", avgVol ? fmt(avgVol) : "-", true],
          ["SOP", base.sop || "-", false],
        ] as [string, string, boolean][]
      ).map(([k, v, accent]) => (
        <div
          key={k}
          className="qd-bandcell"
          style={{
            flex: "1 0 auto",
            padding: "12px 20px",
            border: TABLE_BORDER,
            marginRight: -1,
            marginBottom: -1,
          }}
        >
          <div
            className="qd-bandk"
            style={{ fontSize: 15, color: "var(--text-dim)", marginBottom: 4 }}
          >
            {k}
          </div>
          <div
            className="qd-bandv"
            style={{
              fontSize: 27,
              fontWeight: 700,
              letterSpacing: "-0.01em",
              fontVariantNumeric: "tabular-nums",
              color: accent ? "var(--brand)" : "var(--text-base)",
            }}
          >
            {v}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div
      className="qd-root"
      style={{ padding: "24px 28px 56px", display: "flex", flexDirection: "column", gap: 16 }}
    >
      <div className="qd-noprint">
        <ScreenHelp items={DETAIL_HELP} />
      </div>
      {/* title row */}
      <div
        className="qd-titlerow"
        style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
            <span
              style={{
                fontFamily: "ui-monospace, monospace",
                fontSize: 14,
                color: "var(--brand)",
                fontWeight: 700,
              }}
            >
              {base.id}
            </span>
            <StatusBadge status={status} />
            <span style={{ fontSize: 12, color: "var(--text-faint)" }}>
              v{base.versions.length || 1}
            </span>
          </div>
          <div
            className="qd-title"
            style={{
              fontFamily: "var(--font-title)",
              fontWeight: 800,
              fontSize: 24,
              letterSpacing: "-0.01em",
            }}
          >
            {base.project}
          </div>
          <div
            className="qd-sub"
            style={{ fontSize: 13.5, color: "var(--text-muted)", marginTop: 4 }}
          >
            {custName} · 담당 {base.owner} · 승인 {base.approver}
          </div>
        </div>
        <div
          className="qd-noprint"
          style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}
        >
          {/* 내용추가11 시트5 — 이전 화면(견적 리스트/대시보드)으로 복귀. 리스트 조회 조건은 유지된다. */}
          <Pill variant="ghost" size="sm" icon={<Icon name="list" size={15} />} onClick={onBack}>
            이전 화면
          </Pill>
          <Select
            value={status}
            onChange={pending ? undefined : changeStatus}
            options={(Object.keys(STATUS) as StatusKey[]).map((k) => ({
              value: k,
              label: STATUS[k].label,
            }))}
            style={{ width: 150, height: 40, opacity: pending ? 0.6 : 1 }}
          />
          {status === "draft" && (
            <Pill
              variant="dark"
              size="sm"
              icon={<Icon name="save" size={15} />}
              onClick={() => onEditQuote(base.id)}
            >
              수정
            </Pill>
          )}
          {dirty && (
            <Pill
              variant="dark"
              size="sm"
              disabled={pending}
              icon={<Icon name="save" size={15} />}
              onClick={() => void saveEdits()}
            >
              규격 저장
            </Pill>
          )}
          <Pill
            variant="outlined"
            size="sm"
            icon={<Icon name="download" size={15} />}
            onClick={() => {
              downloadXlsx(
                exportFileName(base.id, isoDate(new Date())),
                quoteDetailSheets(base, custName, procOrder)
              );
              onToast("견적을 엑셀로 내보냈습니다");
            }}
          >
            엑셀
          </Pill>
          <Pill
            variant="outlined"
            size="sm"
            icon={<Icon name="save" size={15} />}
            onClick={async () => {
              try {
                const blob = await exportQuoteBundle(base.id);
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `quote-${base.id}.zip`;
                a.click();
                URL.revokeObjectURL(url);
                onToast("견적 백업 파일을 내보냈습니다 (이미지·참조 포함)");
              } catch (err) {
                onToast(err instanceof Error ? err.message : "내보내기에 실패했습니다");
              }
            }}
          >
            내보내기(백업)
          </Pill>
          <Pill
            variant="primary"
            size="sm"
            icon={<Icon name="file" size={15} />}
            onClick={() => {
              onToast("PDF 견적서 — 인쇄 대화상자를 엽니다");
              window.print();
            }}
          >
            PDF 견적서
          </Pill>
          {/* 이슈 #110 — 고객 제출용 정식 견적서(별도 편집 화면) */}
          <Pill
            variant="dark"
            size="sm"
            icon={<Icon name="file" size={15} />}
            onClick={() => onCustomerDoc(base.id)}
          >
            제출 견적 PDF
          </Pill>
        </div>
      </div>

      {/* header band — 결재라인은 하단 담당자 의견 카드로 이동 */}
      {/* CNC 파트가 있으면 인쇄에선 카드 표 thead 의 밴드가 대신 찍힌다(중복 방지로 숨김). */}
      <div className={hasCnc ? "qd-bandwrap qd-band-inflow" : "qd-bandwrap"}>
        <Card pad={20}>
          <HeaderBand />
          {cust?.biz && (
            <div
              className="qd-noprint"
              style={{
                marginTop: 16,
                paddingTop: 14,
                borderTop: "1px solid var(--bg-card)",
                display: "flex",
                gap: 28,
                fontSize: 12.5,
                color: "var(--text-dim)",
                flexWrap: "wrap",
              }}
            >
              <span>
                사업자번호 <b style={{ color: "var(--text-near)" }}>{cust.biz}</b>
              </span>
              <span>
                대표 <b style={{ color: "var(--text-near)" }}>{cust.ceo}</b>
              </span>
              <span>
                담당 연락처 <b style={{ color: "var(--text-near)" }}>{formatContacts(cust)}</b>
              </span>
            </div>
          )}
        </Card>
      </div>

      {status === "lost" && base.lossReason && (
        <div
          className="qd-noprint"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "14px 20px",
            background: "rgba(243,114,127,0.08)",
            border: "1px solid rgba(243,114,127,0.3)",
            borderRadius: 12,
          }}
        >
          <Icon name="alert" size={18} color="var(--text-negative)" />
          <div style={{ fontSize: 13.5 }}>
            <b style={{ color: "var(--text-negative)" }}>수주 실패 사유</b>{" "}
            <span style={{ color: "var(--text-near)", marginLeft: 8 }}>{base.lossReason}</span>
          </div>
        </div>
      )}

      {/* 견적 상세 항목 (품목별 카드) */}
      <Card pad={20} style={{ overflow: "hidden" }}>
        {breakdown ? (
          <BreakdownSection
            rows={breakdown.rows}
            specs={specs}
            onSpec={setSpec}
            editable={status === "draft" || status === "submitted"}
            files={rowFiles}
            partImages={base.cnc.map((p) => p.selection.partImage ?? "")}
            band={<HeaderBand />}
            procOrder={procOrder}
          />
        ) : (
          <div
            style={{
              padding: "16px 4px",
              textAlign: "center",
              color: "var(--text-faint)",
              fontSize: 13,
            }}
          >
            CNC 파트가 없는 견적입니다 — 원가 분해 표시 대상이 없습니다.
          </div>
        )}
      </Card>

      {/* 손익분석표 */}
      {pnl && (
        <Card pad={0} style={{ overflow: "hidden" }}>
          <div
            className="qd-sectitle"
            style={{
              padding: "18px 24px 12px",
              fontFamily: "var(--font-title)",
              fontWeight: 800,
              fontSize: 16,
            }}
          >
            손익분석표{" "}
            <span style={{ fontSize: 11.5, color: "var(--text-faint)", fontWeight: 400 }}>
              · 아이템별 연도별 물량 · 매출 · 매출이익 (매출 = 아이템 견적가 × 아이템 물량)
            </span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, minWidth: 720 }}
            >
              <thead>
                <tr style={{ background: "var(--bg-card-hover)" }}>
                  <th style={{ ...th, textAlign: "left" }}>품목</th>
                  <th style={{ ...th, textAlign: "left" }}>구분</th>
                  {pnl.years.map((y) => (
                    <th key={y} style={cell}>
                      {y}
                    </th>
                  ))}
                  <th style={{ ...cell, color: "var(--text-base)" }}>합계</th>
                </tr>
              </thead>
              <tbody>
                {/* 아이템별 블록 — 매출 = 그 아이템 견적가 × 그 아이템 물량 */}
                {pnl.parts.map((p, i) => (
                  <PnLBlock
                    key={`${p.name}-${i}`}
                    name={p.name}
                    years={pnl.years}
                    volume={p.years.map((c) => c.volume)}
                    revenue={p.years.map((c) => c.revenue)}
                    profit={p.years.map((c) => c.profit)}
                  />
                ))}
                {/* 최종 합계 = Σ아이템 */}
                <PnLBlock
                  name="합계"
                  years={pnl.years}
                  volume={pnl.years.map((_y, i) => sumYear(pnl, i, "volume"))}
                  revenue={pnl.years.map((_y, i) => sumYear(pnl, i, "revenue"))}
                  profit={pnl.years.map((_y, i) => sumYear(pnl, i, "profit"))}
                  strong
                />
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* 담당자 의견 카드는 요청(내용추가11 시트3)으로 삭제 — 결재라인은 앞서 #145 로 삭제 */}

      {/* versions / logs tabs */}
      <div className="qd-noprint">
        <Card pad={0} style={{ overflow: "hidden" }}>
          <div style={{ display: "flex", borderBottom: "1px solid var(--bg-card)" }}>
            {(
              [
                ["versions", "버전", "history"],
                ["logs", "수정 이력", "clock"],
              ] as ["versions" | "logs", string, "history" | "clock"][]
            ).map(([k, lab, ic]) => (
              <button
                type="button"
                key={k}
                onClick={() => setTab(k)}
                style={{
                  flex: 1,
                  padding: "14px 0",
                  background: "transparent",
                  border: 0,
                  cursor: "pointer",
                  color: tab === k ? "var(--text-base)" : "var(--text-dim)",
                  fontWeight: tab === k ? 700 : 400,
                  fontSize: 13.5,
                  fontFamily: "inherit",
                  borderBottom: tab === k ? "2px solid var(--brand)" : "2px solid transparent",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 7,
                }}
              >
                <Icon name={ic} size={15} />
                {lab}
              </button>
            ))}
          </div>
          <div style={{ padding: 20 }}>
            {tab === "versions" ? (
              <div style={{ display: "flex", flexDirection: "column" }}>
                {versions
                  .slice()
                  .reverse()
                  .map((v, i, arr) => (
                    <div
                      key={v.v}
                      style={{
                        display: "flex",
                        gap: 12,
                        paddingBottom: i === arr.length - 1 ? 0 : 18,
                      }}
                    >
                      <div
                        style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
                      >
                        <div
                          style={{
                            width: 30,
                            height: 30,
                            borderRadius: "50%",
                            background: i === 0 ? "var(--brand)" : "var(--bg-card)",
                            color: i === 0 ? "#ffffff" : "var(--text-base)",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 800,
                            fontSize: 12,
                          }}
                        >
                          {v.v}
                        </div>
                        {i !== arr.length - 1 && (
                          <div
                            style={{
                              flex: 1,
                              width: 2,
                              background: "var(--bg-card)",
                              marginTop: 4,
                            }}
                          />
                        )}
                      </div>
                      <div style={{ paddingTop: 3 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 700 }}>{v.note}</div>
                        <div style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 2 }}>
                          {v.by} · {v.at}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {base.logs
                  .slice()
                  .reverse()
                  .map((l) => (
                    <div key={`${l.at}-${l.what}`} style={{ display: "flex", gap: 11 }}>
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          background: "var(--brand)",
                          color: "#ffffff",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 800,
                          fontSize: 12,
                          flexShrink: 0,
                        }}
                      >
                        {l.who[0]}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, color: "var(--text-strong)" }}>
                          <b>{l.who}</b> · {l.what}
                        </div>
                        <div style={{ fontSize: 11.5, color: "var(--text-faint)", marginTop: 2 }}>
                          {l.at}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </Card>
      </div>

      {askLoss && (
        <div
          onClick={() => setAskLoss(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            zIndex: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 400,
              maxWidth: "calc(100vw - 32px)",
              background: "var(--bg-surface)",
              borderRadius: 12,
              boxShadow: "rgba(0,0,0,0.5) 0px 8px 24px",
              padding: 24,
            }}
          >
            <div
              style={{
                fontFamily: "var(--font-title)",
                fontWeight: 800,
                fontSize: 17,
                marginBottom: 6,
              }}
            >
              수주 실패 사유 선택
            </div>
            <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 18 }}>
              실패 사유는 손익·수주 분석 통계에 활용됩니다.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
              {lossReasonsRes.data.map((r) => (
                <button
                  type="button"
                  key={r}
                  onClick={() => setLossReason(r)}
                  style={{
                    textAlign: "left",
                    padding: "12px 14px",
                    borderRadius: 6,
                    border: 0,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: 13.5,
                    background: lossReason === r ? "rgba(243,114,127,0.15)" : "var(--bg-surface-2)",
                    color: lossReason === r ? "var(--text-negative)" : "var(--text-near)",
                    fontWeight: lossReason === r ? 700 : 400,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <span
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      border: `2px solid ${lossReason === r ? "var(--text-negative)" : "var(--text-ghost)"}`,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {lossReason === r && (
                      <span
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          background: "var(--text-negative)",
                        }}
                      />
                    )}
                  </span>
                  {r}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <Pill variant="ghost" size="sm" onClick={() => setAskLoss(false)}>
                취소
              </Pill>
              <Pill
                variant="primary"
                size="sm"
                disabled={!lossReason || pending}
                onClick={() => {
                  if (lossReason) {
                    setAskLoss(false);
                    void commitStatus("lost", lossReason);
                  }
                }}
              >
                확인
              </Pill>
            </div>
          </div>
        </div>
      )}

      {/* PDF 인쇄 — 가로 A4, 앱 chrome 숨김, thead 매 페이지 반복.
          기본정보 반복: 견적 상세 항목 표(qd-grid)의 <thead> 첫 행에 헤더밴드를 넣어
          매 페이지 상단에 출력(화면에서는 그 행 숨김, 상단 헤더 카드는 인쇄에서 숨김). */}
      <style>
        {`.qd-grid>thead>tr.qd-bandrow{display:none}
        .qd-grid>thead>tr.qd-headrow>td{padding:0 0 12px}
        .qd-grid .qd-thumbbox,.qd-grid img{max-width:100%}
        .qd-printonly{display:none}
        /* 손익 아이템명 — 사용자가 넣은 개행을 화면·인쇄 모두에서 그대로.
           td 를 하나 더 붙여 (0,2,1) — cell 인라인 nowrap 을 이긴다. */
        .qd-root td.qd-ml{white-space:pre-line!important;overflow-wrap:anywhere;word-break:break-word}
        @media print{
          .qd-printonly{display:inline}
          @page{size:A4 landscape;margin:10mm}
          .qd-noprint{display:none!important}
          nav,aside,header{display:none!important}
          .qd-root{padding:0!important}
          body{background:#fff!important}
          table thead{display:table-header-group}
          .qd-root tr,.qd-root td,.qd-root th{break-inside:avoid}
          .qd-root table{font-size:12px!important;min-width:0!important}
          .qd-root td,.qd-root th{padding:8px 6px!important}
          /* 인쇄 굵기·색 통일 — 모노 숫자폰트가 본문보다 굵게 찍히고 셀별 강조(굵게·
             파랑 견적가·빨강 이익률)가 뒤섞여 보이는 문제. 폰트 하나·잉크색 하나로,
             td 는 weight 를 테이블(굵게 토글)에서 상속받아 전 셀 동일하게. */
          .qd-root table th,.qd-root table td{font-family:var(--font-ui)!important;color:#000!important}
          .qd-root table td{font-weight:inherit!important}
          /* 상단(제목·요약 카드) 압축 — 내용추가16: 높이·글자를 줄여 1페이지에 상세 항목표를
             크게 싣는다(요약 카드는 한 줄, 화면 인라인 크기를 !important 로 덮음). */
          .qd-root{gap:6px!important}
          .qd-titlerow{gap:8px!important}
          .qd-title{font-size:17px!important}
          .qd-bandwrap>div{padding:0!important;border-radius:0!important}
          .qd-band{flex-wrap:nowrap!important}
          .qd-bandcell{flex:1 1 auto!important;padding:4px 10px!important}
          .qd-bandk{font-size:10px!important;margin-bottom:1px!important}
          .qd-bandv{font-size:13px!important}
          .qd-sectitle{padding:6px 4px 4px!important;font-size:13px!important;break-after:avoid}
          /* 내용추가17 — 상단의 견적번호·상태·버전 줄과 고객사·담당·승인 줄은 인쇄에서 삭제 */
          .qd-titlerow>div>div:first-child,.qd-sub{display:none!important}
          .qd-band-inflow{display:none!important}
          /* 다크 테마에서 인쇄해도 잉크색 — 글자색 토큰을 라이트 값으로(밴드·제목이 흰 글씨로 사라짐) */
          .qd-root{--text-base:#171719;--text-strong:#2e2f33;--text-near:#46474c;--text-muted:#5a5c63;--text-dim:#70737c;color:var(--text-base)}
          .qd-grid>thead>tr.qd-bandrow{display:table-row}
          /* 밴드 값은 한 줄(내용 폭 고정) — 남는 폭은 프로젝트명(2번째 칸)이 쓰고, 좁으면 그 칸만
             줄바꿈. 밴드가 너무 높아지면 Chrome 이 thead 반복을 포기하므로 높이를 낮게 유지한다. */
          .qd-bandv{white-space:nowrap}
          .qd-band>.qd-bandcell{flex:0 0 auto!important}
          .qd-band>.qd-bandcell:nth-child(2){flex:1 1 auto!important;min-width:0}
          .qd-bandcell:nth-child(2) .qd-bandv{white-space:normal}
          /* 견적 상세 항목 — 고정 9열 표. 품목 행은 페이지 사이에서 잘리지 않고, thead(밴드·
             섹션 제목·열 머리글)가 매 페이지 반복된다. 글자배율(A−/A+)은 --qd-scale 로 인쇄에도 반영. */
          .qd-gridwrap{overflow:visible!important}
          /* 표 11px — 제목 17px 대비 0.65(카톡 피드백 "제목 20이면 상세 13") */
          .qd-root table.qd-grid{font-size:calc(11px * var(--qd-scale, 1))!important;min-width:0!important}
          .qd-root table.qd-grid>thead>tr.qd-bandrow>td{padding:0 0 6px!important;border:none!important}
          .qd-root table.qd-grid>thead>tr.qd-headrow>td{padding:0 0 4px!important;border:none!important}
          .qd-root table.qd-grid th{padding:5px 6px!important;background:transparent!important;border-color:#000!important}
          .qd-root table.qd-grid>tbody td{padding:6px 8px!important}
          .qd-root table.qd-grid>tbody td.qd-imgcell{padding:6px 3px!important}
          .qd-grid>tbody tr.qd-item>td{border-top:1.5px solid #000!important}
          /* 자리표시·PDF 칩은 96:68 상자 비율로 축소. 도면 이미지는 열 폭에 맞추고 높이는 원본 비율
             (contain — 잘리지 않음), 세로로 긴 도면은 최대 40mm 에서 비율 유지 축소. */
          .qd-grid .qd-thumbbox{max-width:100%!important;height:auto!important;aspect-ratio:96/68}
          .qd-grid img.qd-thumbimg{width:100%!important;max-width:100%!important;height:auto!important;max-height:40mm!important;object-fit:contain!important}
          .qd-celltotal{border-top-color:#000!important}
        }`}
      </style>
    </div>
  );
};

// ---- 손익분석표 행 헬퍼 (집계는 projection 이 이미 한 값 — 화면은 합산만) ----
function sumYear(
  pnl: ProjectionResult,
  yearIdx: number,
  pick: "volume" | "revenue" | "profit"
): number {
  return pnl.parts.reduce((acc, p) => acc + (p.years[yearIdx]?.[pick] ?? 0), 0);
}
const sumArr = (a: number[]): number => a.reduce((x, y) => x + y, 0);
const rateText = (revenue: number, profit: number): string =>
  revenue > 0 ? `${((profit / revenue) * 100).toFixed(1)}%` : "-";

const metricCell = {
  ...cell,
  textAlign: "left" as const,
  fontFamily: "inherit",
  fontWeight: 700,
  color: "var(--text-muted)",
};

// 아이템(또는 합계) 1개 블록 = 물량/매출/이익/이익률 4행. 값은 이미 계산된 연도별 배열.
const PnLBlock = ({
  name,
  years,
  volume,
  revenue,
  profit,
  strong = false,
}: {
  name: string;
  years: number[];
  volume: number[];
  revenue: number[];
  profit: number[];
  strong?: boolean;
}) => {
  const rowStyle = {
    borderTop: "1px solid var(--bg-surface-2)",
    ...(strong ? { background: "var(--bg-card-hover)" } : {}),
  };
  const nameCell = (
    <td
      rowSpan={4}
      className="qd-ml"
      style={{
        ...cell,
        textAlign: "left",
        fontFamily: "inherit",
        fontWeight: 800,
        borderTop: "2px solid var(--bg-surface-2)",
      }}
    >
      {name?.trim()}
    </td>
  );
  // 원 단위 전체 표기 — 축약(fmtComp) 대신 천단위 콤마(fmt)
  const metrics: { label: string; vals: number[] }[] = [
    { label: "연간 물량(EA)", vals: volume },
    { label: "매출액", vals: revenue },
    { label: "매출이익액", vals: profit },
  ];
  return (
    <>
      {metrics.map((m, mi) => (
        <tr key={m.label} style={rowStyle}>
          {mi === 0 && nameCell}
          <td style={metricCell}>{m.label}</td>
          {years.map((y, i) => (
            <td key={y} style={cell}>
              {fmt(m.vals[i] ?? 0)}
            </td>
          ))}
          <td style={{ ...cell, fontWeight: 800 }}>{fmt(sumArr(m.vals))}</td>
        </tr>
      ))}
      <tr style={rowStyle}>
        <td style={metricCell}>매출이익률(%)</td>
        {years.map((y, i) => (
          <td key={y} style={cell}>
            {rateText(revenue[i] ?? 0, profit[i] ?? 0)}
          </td>
        ))}
        <td style={{ ...cell, fontWeight: 800 }}>{rateText(sumArr(revenue), sumArr(profit))}</td>
      </tr>
    </>
  );
};
